// src/store/modules/fonts.js
//
// Supplies the company's licensed fonts to the designer.
//
// ElementProperties has been reading store.getters['fonts/fonts'] for a
// while, but this module never existed — the getter resolved to
// undefined, `|| []` swallowed it, and the font dropdown silently
// contained nothing but the default. That is why a company with three
// PPRightGrotesk rows in customer_fonts saw only Inter on the web while
// its tablet showed all three.
//
// Rows come from GET /api/customer-fonts, the same endpoint the Flutter
// app uses, scoped to the caller's company by the auth token. Shape:
//   { fontId, customer_id, font_key, font_label, zpl_id, sort_order }

import { defaultFontKeyFor } from '@/utils/fontManifest'

// Reuses the existing service rather than calling axios here, so the
// auth token and 401 handling stay in one place (api.js interceptors).
import { fetchCustomerFonts } from '@/services/customerFonts.js'

export default {
  namespaced: true,

  state: () => ({
    fonts: [],
    loading: false,

    // A failed load is NOT an empty font list, and the two must not
    // look alike. Empty means "this company has no licensed fonts, use
    // Inter". Failed means we do not know what it has — and defaulting
    // to Inter there is actively wrong for a company that owns fonts.
    // The tablet learned this the hard way when a mis-cased column name
    // in the API query surfaced as "why am I being offered Inter?"
    // rather than as an error.
    loadFailed: false,
    loadError: '',
  }),

  getters: {
    // The name ElementProperties already reads.
    fonts: state => state.fonts,

    loading:    state => state.loading,
    loadFailed: state => state.loadFailed,
    loadError:  state => state.loadError,

    // The font_key an element with no font_key of its own renders in.
    // Company's primary font, or Inter when it has none.
    defaultFontKey: state => defaultFontKeyFor(state.fonts),
  },

  mutations: {
    SET_LOADING(state, value) {
      state.loading = value
    },
    SET_FONTS(state, fonts) {
      state.fonts      = fonts
      state.loadFailed = false
      state.loadError  = ''
    },
    SET_LOAD_FAILED(state, message) {
      state.fonts      = []
      state.loadFailed = true
      state.loadError  = message
    },
  },

  actions: {
    async fetchFonts({ commit, state }, { force = false } = {}) {
      // Fonts change about once per customer lifetime, so one fetch per
      // session is plenty. Pass force: true after an admin edits them.
      if (state.fonts.length > 0 && !force) return state.fonts

      commit('SET_LOADING', true)
      try {
        const rows = await fetchCustomerFonts()
        commit('SET_FONTS', rows)
        return rows
      } catch (e) {
        commit('SET_LOAD_FAILED', e?.message || String(e))
        return []
      } finally {
        commit('SET_LOADING', false)
      }
    },
  },
}